import React, { FC, useState, useEffect, useMemo } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import {
  Menu,
  Button,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  ListItemText,
  ListItemIcon,
  MenuList,
  Typography,
  Switch,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import BugReportIcon from '@mui/icons-material/BugReport';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { Box } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { TFunction } from 'i18next';
import { loggerSaver } from 'lib/EventsProvider';
import LanguageIcon from '@mui/icons-material/Language';
import ROUTE_PATHS from 'routes/values';
import { IEventSaveLoggerlist } from 'lib/EventsProvider/@types';
import { clearSession } from 'helpers';
import LoggerWrapper, { MyText2 } from './Logger.styled';
import i18n, { setLang } from '../../i18n';
import { APP_INFO, IS_PROD } from '../../config';
import RenderIf from '../RenderIf/RenderIf.component';
import EventLogsComponent from './components/EventLogs/EventLogs.component';
import {
  LOGGER_FORCE_HIDE_LOCAL_STORE_KEY,
  LOGGER_SHOW_EVENTS_LOCAL_STORE_KEY,
  LOGGER_SHOW_LOGS_LOCAL_STORE_KEY,
  LOGGER_SHOW_MINIMAL_LOCAL_STORE_KEY,
} from './Logger.values';

interface ILoggerProps {
  [x: string]: unknown;
}

const LoggerComponent: FC<ILoggerProps> = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentLang, setCurrentLang] = useState<string>(i18n.language || 'en');
  const open = Boolean(anchorEl);

  const [showEventsLog, setShowEventsLog] = useState<boolean>(
    window.localStorage.getItem(LOGGER_SHOW_EVENTS_LOCAL_STORE_KEY) === 'true',
  );
  const [showLogs, setShowLogs] = useState<boolean>(
    window.localStorage.getItem(LOGGER_SHOW_LOGS_LOCAL_STORE_KEY) === 'true',
  );
  const [showMinimal, setShowMinimal] = useState<boolean>(
    window.localStorage.getItem(LOGGER_SHOW_MINIMAL_LOCAL_STORE_KEY) === 'true',
  );
  const [forceHide] = useState<boolean>(window.localStorage.getItem(LOGGER_FORCE_HIDE_LOCAL_STORE_KEY) === 'true');
  const [eventLogs, setEventLogs] = useState<IEventSaveLoggerlist[]>([]);
  const [showAllEventsLog, setShowAllEventsLog] = useState<boolean>(false);

  const handleChangeSwitchEventsLog = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setShowEventsLog(event.target.checked);
    window.localStorage.setItem(LOGGER_SHOW_EVENTS_LOCAL_STORE_KEY, event.target.checked.toString());
  };

  const handleChangeSwitchLogs = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setShowLogs(event.target.checked);
    window.localStorage.setItem(LOGGER_SHOW_LOGS_LOCAL_STORE_KEY, event.target.checked.toString());
  };

  const handleChangeShowMinimal = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setShowMinimal(event.target.checked);
    window.localStorage.setItem(LOGGER_SHOW_MINIMAL_LOCAL_STORE_KEY, event.target.checked.toString());
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const setLanguage = (lang: string): Promise<TFunction<'translation', undefined>> => setLang(lang);

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleOnChangeLang = (event: React.MouseEvent<HTMLElement>, newLang: string): void => {
    setLanguage(newLang);
    setCurrentLang(newLang);
  };

  useMemo(() => {
    if (showEventsLog && eventLogs.length) {
      const last = [...eventLogs].pop();
      toast(`EventReport::${last?.tag}`, {
        position: 'top-right',
        autoClose: 700,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
        theme: 'light',
        toastId: last?.id,
        pauseOnFocusLoss: true,
        bodyClassName: 'eventsLogs',
      });
    }
  }, [eventLogs, showEventsLog]);

  useEffect(() => {
    const sub = loggerSaver.getEvents().subscribe((value: IEventSaveLoggerlist[]) => {
      setEventLogs([...(value || [])]);
    });
    return () => sub.unsubscribe();
    /* eslint-disable-next-line */
  }, []);

  const handleOnToggleDrawer = (value: boolean): void => {
    handleClose();
    setShowAllEventsLog(value);
  };

  const handleOnCleanSession = (): void => {
    clearSession();
    window.location.href = ROUTE_PATHS.SIGN_UP;
  };

  return (
    <RenderIf condition={!IS_PROD && !forceHide}>
      <>
        <div id="EventLostToastContainer">
          <ToastContainer />
        </div>
        <LoggerWrapper
          data-testid="LoggerComponent"
          className="LoggerWrapper"
          sx={{
            position: 'fixed',
            top: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(3),
          }}
        >
          <Box>
            <IconButton
              id="LoggerComponentOpenBtn"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          <RenderIf condition={!showMinimal}>
            <>
              <Box>
                <Chip
                  size="small"
                  component="span"
                  label={APP_INFO.versionComplete}
                  color="secondary"
                  sx={{
                    fontSize: '10px',
                  }}
                />
              </Box>
              <Box mt={0.5}>
                <Chip
                  size="small"
                  component="span"
                  label={APP_INFO.buildName}
                  color="primary"
                  sx={{
                    fontSize: '10px',
                  }}
                />
              </Box>
            </>
          </RenderIf>
          <Menu
            data-testid="LoggerComponent_Menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuList
              sx={{
                width: 280,
                maxWidth: '100%',
                padding: 0,
              }}
            >
              <MenuItem>
                <ListItemIcon>
                  <IndeterminateCheckBoxIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography fontSize={(theme) => theme.typography.pxToRem(10)}>Minimal</Typography>
                </ListItemText>
                <Box>
                  <Switch
                    checked={showMinimal}
                    onChange={handleChangeShowMinimal}
                    inputProps={{
                      'aria-label': 'controlled',
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem>
                <ListItemIcon>
                  <LanguageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <MyText2 fontSize={(theme) => theme.typography.overline.fontSize}>Change lang</MyText2>
                </ListItemText>
                <ToggleButtonGroup
                  value={currentLang}
                  exclusive
                  onChange={handleOnChangeLang}
                  aria-label="text alignment"
                >
                  <ToggleButton
                    sx={{
                      padding: 1,
                    }}
                    value="en"
                    aria-label="left aligned"
                  >
                    <Typography fontSize={(theme) => theme.typography.pxToRem(10)}>En</Typography>
                  </ToggleButton>
                  <ToggleButton
                    sx={{
                      padding: 1,
                    }}
                    value="es"
                    aria-label="centered"
                  >
                    <Typography fontSize={(theme) => theme.typography.pxToRem(10)}>Es</Typography>
                  </ToggleButton>
                  <ToggleButton
                    sx={{
                      padding: 1,
                    }}
                    value="pt"
                    aria-label="right aligned"
                  >
                    <Typography fontSize={(theme) => theme.typography.pxToRem(10)}>Pt</Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </MenuItem>
              <MenuItem>
                <ListItemIcon>
                  <BugReportIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <MyText2 fontSize={(theme) => theme.typography.overline.fontSize}>Events Logs</MyText2>
                </ListItemText>
                <Box>
                  <Switch
                    checked={showEventsLog}
                    onChange={handleChangeSwitchEventsLog}
                    inputProps={{
                      'aria-label': 'controlled',
                    }}
                  />
                  <Button
                    className="LoggerComponent_btnToggleShowAllEvents"
                    variant="outlined"
                    size="small"
                    // onClick={handleOnToggleEventLogs}
                    onClick={() => handleOnToggleDrawer(true)}
                  >
                    <MyText2 fontSize={8}>Show All</MyText2>
                  </Button>
                </Box>
              </MenuItem>
              <MenuItem>
                <ListItemIcon>
                  <HistoryEduIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <Typography fontSize={(theme) => theme.typography.pxToRem(10)}>
                    Toggle Logs: (Required reload)
                  </Typography>
                </ListItemText>
                <Box>
                  <Switch
                    checked={showLogs}
                    onChange={handleChangeSwitchLogs}
                    inputProps={{
                      'aria-label': 'controlled',
                    }}
                  />
                </Box>
              </MenuItem>
              <MenuItem>
                <ListItemIcon>
                  <CleaningServicesIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <MyText2 fontSize={(theme) => theme.typography.overline.fontSize}>Clean session</MyText2>
                </ListItemText>
                <Box>
                  <Button
                    className="LoggerComponent_btnCleanSession"
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => handleOnCleanSession()}
                  >
                    <Typography
                      fontSize={(theme) => theme.typography.pxToRem(10)}
                      color={(theme) => theme.palette.error.contrastText}
                    >
                      Clean Session
                    </Typography>
                  </Button>
                </Box>
              </MenuItem>
              {/* <Divider /> */}
            </MenuList>
          </Menu>
          <EventLogsComponent
            eventList={eventLogs}
            open={showAllEventsLog}
            onClose={() => handleOnToggleDrawer(false)}
            onOpen={() => handleOnToggleDrawer(true)}
          />
        </LoggerWrapper>
      </>
    </RenderIf>
  );
};

export default LoggerComponent;
export type { ILoggerProps };
